// src/components/context/preloader.tsx

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'

type PreloaderContextType = {
  isLoading: boolean
  start: () => void
  stop: () => void
  total: number
  percentage: number
}

const PreloaderContext = createContext<PreloaderContextType | undefined>(
  undefined
)

type Props = {
  children: ReactNode
}

export const PreloaderProvider = ({ children }: Props) => {
  const [pendingCount, setPendingCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const percentage =
    totalCount === 0
      ? 0
      : Math.round(((totalCount - pendingCount) / totalCount) * 100)

  const start = useCallback(() => {
    setPendingCount(prev => prev + 1)
    setTotalCount(prev => prev + 1)
  }, [])

  const stop = useCallback(() => {
    setPendingCount(prev => (prev > 0 ? prev - 1 : 0))
  }, [])

  const value: PreloaderContextType = {
    isLoading: pendingCount > 0,
    start,
    stop,
    total: totalCount,
    percentage,
  }

  return (
    <PreloaderContext.Provider value={value}>
      {children}
    </PreloaderContext.Provider>
  )
}

export const usePreloader = () => {
  const context = useContext(PreloaderContext)

  if (!context)
    throw new Error('usePreloader must be used within PreloaderProvider')

  return context
}
